import React from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, DragIndicator } from "@mui/icons-material";
import OutputPanel from "./OutputPanel";

/**
 * Floating Output Window Component - Draggable and resizable floating window
 */
const FloatingOutputWindow = ({
  isFloating,
  position,
  size,
  onDragStart,
  onResizeStart,
  onClose,
  children,
  // Output panel props
  showOutput,
  setShowOutput,
  activeTab,
  setActiveTab,
  testCases,
  customInput,
  setCustomInput,
  customResult,
  customLoading,
  onRunCustomInput,
  results,
  summary,
  showCelebration,
  setShowCelebration,
  onFloatingToggle,
  vtLoading,
}) => {
  // No internal blocking - controlled by parent
  if (!isFloating || !showOutput) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        // Responsive positioning: Centered on mobile, Custom on desktop
        top: { base: "50%", md: position.y },
        left: { base: "50%", md: position.x },
        transform: { base: "translate(-50%, -50%)", md: "none" },
        width: { base: "95vw", md: size.width },
        height: { base: "90vh", md: size.height },
        maxWidth: "100vw",
        maxHeight: "100vh",
        zIndex: 1000,
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow:
          "0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 196, 154, 0.1)",
        border: "1px solid rgba(0, 196, 154, 0.2)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
    >
      {/* Floating Output Drag Handle */}
      <Box
        onMouseDown={onDragStart}
        sx={{
          height: 40,
          background: "linear-gradient(135deg, #00C49A 0%, #00a085 100%)",
          cursor: { base: "default", md: "move" }, // No move cursor on mobile
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          zIndex: 1,
          flexShrink: 0,
          "&:hover": {
            background: "linear-gradient(135deg, #00a085 0%, #008a73 100%)",
          },
        }}
      >
        <DragIndicator sx={{ color: "white", fontSize: 20 }} />
        <Typography variant="caption" sx={{ color: "white", fontWeight: 600 }}>
          Output - Floating Mode
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
            },
          }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Resize Handle - Corner (Bottom Right) with Diagonal Lines */}
      <Box
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onResizeStart(e, "se-resize");
        }}
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 20,
          height: 20,
          cursor: "se-resize",
          zIndex: 2000,
          backgroundColor: "transparent",
          pointerEvents: "auto",
          display: { base: "none", md: "block" }, // Hide resize handle on mobile
          "&:hover": {
            backgroundColor: "rgba(0, 196, 154, 0.1)",
          },
          // Standard resize icon: diagonal lines pattern
          "&::before": {
            content: '""',
            position: "absolute",
            bottom: 2,
            right: 2,
            width: 16,
            height: 16,
            backgroundImage: `
              linear-gradient(135deg, rgba(0, 0, 0, 0.3) 25%, transparent 25%),
              linear-gradient(135deg, transparent 75%, rgba(0, 0, 0, 0.3) 75%),
              linear-gradient(135deg, rgba(0, 0, 0, 0.3) 25%, transparent 25%),
              linear-gradient(135deg, transparent 75%, rgba(0, 0, 0, 0.3) 75%)
            `,
            backgroundSize: "4px 4px",
            backgroundPosition: "0 0, 2px 2px, 0 0, 2px 2px",
          },
          "&:hover::before": {
            backgroundImage: `
              linear-gradient(135deg, rgba(0, 196, 154, 0.8) 25%, transparent 25%),
              linear-gradient(135deg, transparent 75%, rgba(0, 196, 154, 0.8) 75%),
              linear-gradient(135deg, rgba(0, 196, 154, 0.8) 25%, transparent 25%),
              linear-gradient(135deg, transparent 75%, rgba(0, 196, 154, 0.8) 75%)
            `,
          },
        }}
      />

      {/* Resize Handle - Right Edge */}
      <Box
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onResizeStart(e, "ew-resize");
        }}
        sx={{
          position: "absolute",
          top: 40,
          bottom: 24,
          right: 0,
          width: 10,
          cursor: "ew-resize",
          zIndex: 2000,
          backgroundColor: "transparent",
          pointerEvents: "auto",
          display: { base: "none", md: "block" }, // Hide resize handle on mobile
          "&:hover": {
            backgroundColor: "rgba(0, 196, 154, 0.2)",
          },
        }}
      />

      {/* Resize Handle - Bottom Edge */}
      <Box
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onResizeStart(e, "ns-resize");
        }}
        sx={{
          position: "absolute",
          bottom: 0,
          left: 24,
          right: 24,
          height: 10,
          cursor: "ns-resize",
          zIndex: 2000,
          backgroundColor: "transparent",
          pointerEvents: "auto",
          display: { base: "none", md: "block" }, // Hide resize handle on mobile
          "&:hover": {
            backgroundColor: "rgba(0, 196, 154, 0.2)",
          },
        }}
      />

      {/* Floating Output Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "#f8f9fa",
          position: "relative",
          zIndex: 1,
        }}
      >
        <OutputPanel
          showOutput={showOutput}
          setShowOutput={setShowOutput}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          testCases={testCases}
          customInput={customInput}
          setCustomInput={setCustomInput}
          customResult={customResult}
          customLoading={customLoading}
          onRunCustomInput={onRunCustomInput}
          results={results}
          summary={summary}
          showCelebration={showCelebration}
          setShowCelebration={setShowCelebration}
          isOutputFloating={isFloating}
          onFloatingToggle={onFloatingToggle}
          vtLoading={vtLoading}
          // Responsive output height calculation
          outputHeight={
            // On mobile, height is 90vh - 40px (header). On desktop, size.height - 40px
            // Since we can't easily access vh here without hooks, and size is responsive,
            // we rely on size.height prop. But on mobile size.height logic in parent might be different?
            // Actually, we set height: "90vh" in parent Box style for mobile.
            // OutputPanel needs outputHeight for its internal scroll area.
            // We can pass "100%" if OutputPanel supports it.
            // Assuming OutputPanel uses fixed px height.
            // Let's keep size.height - 40 for now, assumes parent updates size or effective height matches.
            size.height - 40
          }
        />
      </Box>
    </Box>
  );
};

export default FloatingOutputWindow;
